import { h, Fragment } from "preact";

import "./Schedule.scss";
import { forwardRef, useMemo } from "preact/compat";
import { useQuery } from "@urql/preact";
import { DateTime } from "luxon";
import { useEffectEveryHourOnTheHour } from "../../lib/useEffectEveryHourOnTheHour";

const ScheduleQuery = `
  query {
    currentAndNext(n: 2) {
      next {
        ... on Timeslot {
          itemId
          title
          description
          season {
            credits {
              User {
                fname
                sname
              }
            }
            show {
              itemId
              photo
            }
          }
          startTime
          endTime
          webpage
        }
        ... on CurrentAndNext_PseudoTimeslot {
          title
          desc
          photo
          start_time
          end_time
        }
      }
    }
  }
`;

type ScheduleItemInfo =
  | {
      itemId: string;
      title: string;
      description: string;
      season: {
        credits: [{ User: { fname: string; sname: string } }];
        show: { itemId: string; photo: string | null };
      };
      startTime: string;
      endTime: string;
      webpage: string;
    }
  | {
      title: string;
      desc: string;
      photo: string;
      start_time: string | null;
      end_time: string;
    };

interface ScheduleInfo {
  currentAndNext: {
    next: ScheduleItemInfo[];
  };
}

function ScheduleItem({ item }: { item: ScheduleItemInfo }) {
  const image =
    "https://ury.org.uk" +
    ("photo" in item ? item.photo : item.season.show.photo);


  const startDateTime = useMemo<DateTime | null>(() => {
    if ("start_time" in item) {
      if (item.start_time === null) {
        return null;
      }
      return DateTime.fromISO(item.start_time);
    }
    return DateTime.fromISO(item.startTime);
  }, [item]);

  const startTimeStr = useMemo<string | null>(() => {
    const now = DateTime.local();
    return startDateTime.toLocaleString(
      now.day !== startDateTime.day
        ? DateTime.DATETIME_SHORT
        : DateTime.TIME_24_SIMPLE
    );
  }, [item]);



  const endDateTime = useMemo<DateTime | null>(() => {
    if ("end_time" in item) {
      if (/^\d+$/.test(item.end_time)) {
        return DateTime.fromSeconds(parseInt(item.end_time, 10));
      }
      return item.end_time;
    }
    return DateTime.fromISO(item.endTime);
  }, [item]);

  const endTimeStr = useMemo(() => {
    if (endDateTime instanceof DateTime) {
      const diff = endDateTime.diff(startDateTime, "days").toObject().days
      return endDateTime.toLocaleString(
          diff < 1 ? DateTime.TIME_24_SIMPLE : DateTime.DATETIME_SHORT
      );
    }
    return null;
  }, [item]);

  return (
    <div class={"schedule-item" + ("webpage" in item ? " link" : "")} onClick={() => "season" in item ? window.open("https://ury.org.uk/schedule/shows/" + item.season.show.itemId, "_blank") : ""}>
      <img src={image} alt={"Show image for " + item.title} />
      <div class="meta">
        <h3 class="title">{item.title}</h3>
        {"season" in item && (
          <span class="people">
            with{" "}
            {item.season.credits
              .map((x) => x.User.fname + " " + x.User.sname)
              .join(", ")}
          </span>
        )}
        <span class="time">
          {!!startTimeStr ? (
            <Fragment>
              {startTimeStr} - {endTimeStr}
            </Fragment>
          ) : (
            <Fragment>until {endTimeStr}</Fragment>
          )}
        </span>
      </div>
      {"desc" in item ? (
        <p class="desc">{item.desc}</p>
      ) : (
        <div
          class="desc"
          dangerouslySetInnerHTML={{ __html: item.description }}
        />
      )}
    </div>
  );
}

const Schedule = forwardRef<HTMLDivElement>((props, ref) => {
  const [result, refetch] = useQuery<ScheduleInfo>({
    query: ScheduleQuery,
    requestPolicy: 'network-only'
  });
  useEffectEveryHourOnTheHour(refetch);
  if (!result.data) {
    return (
      <div class="schedule card" ref={ref}>
        <a href="https://ury.org.uk/schedule" target="_blank" title="Go to the URY Schedule">
            <h2 class="header-sideline">
                <span class="btn header">
                    Coming Up
                </span>
                <span class="sideline">on URY</span>
            </h2>
        </a>
        <b className="loading">Loading...</b>
      </div>
    );
  }
  return (
    <div class="schedule card" ref={ref}>
      <a href="https://ury.org.uk/schedule" target="_blank" title="Go to the URY Schedule">
          <h2 class="header-sideline">
              <span class="btn header">
                  Schedule
              </span>
              <span class="sideline">Coming up On Air...</span>
          </h2>
      </a>
      { result.data.currentAndNext.next &&
        <div class="schedule-list">
          { result.data.currentAndNext.next.map((item, idx) => (
            <ScheduleItem key={"itemId" in item ? item.itemId : "$" + idx} item={item} />
          )) }
        </div>
      }
      { !result.data.currentAndNext.next &&
        <div>
          <p>There's nothing here at the moment... We'll have more great shows soon!</p>
          <a href="https://ury.org.uk/ontap" target="_blank" class="header-sideline" title="Go to URY's Catchup Service, URY On Tap.">
              <span class="sideline"><strong>Why not catch up with</strong></span>
              <span class="btn header">
                  URY On Tap?
              </span>
          </a>
        </div>
      }
    </div>
  );
});

export default Schedule;
